import type {
  OpportunityAmountMode,
  OpportunityFormatKey,
  OpportunityPricingInputs,
} from "../../types/opportunity.js";

export type CalcLineIdentity = {
  id: string;
  label: string;
};

export type RemapCalcOverridesResult = {
  values: Record<string, number>;
  modes: Record<string, "fixed" | "percent">;
  notes: Record<string, string>;
  keptLabels: string[];
  droppedLabels: string[];
};

function normalizeLabel(raw: string): string {
  return raw.trim().toLowerCase().replace(/\s+/g, " ");
}

/**
 * Carry calc-line overrides onto a new product type: same line id stays,
 * otherwise match by label. Unmatched overrides are dropped (caller warns).
 */
export function remapPricingCalcOverrides(args: {
  previousLines: ReadonlyArray<CalcLineIdentity>;
  nextLines: ReadonlyArray<CalcLineIdentity>;
  values: Record<string, number>;
  modes: Record<string, "fixed" | "percent">;
  notes: Record<string, string>;
}): RemapCalcOverridesResult {
  const prevById = new Map(args.previousLines.map((l) => [l.id, l]));
  const nextById = new Map(args.nextLines.map((l) => [l.id, l]));
  const nextByLabel = new Map<string, CalcLineIdentity>();
  for (const line of args.nextLines) {
    const key = normalizeLabel(line.label);
    if (key && !nextByLabel.has(key)) nextByLabel.set(key, line);
  }

  const values: Record<string, number> = {};
  const modes: Record<string, "fixed" | "percent"> = {};
  const notes: Record<string, string> = {};
  const keptLabels: string[] = [];
  const droppedLabels: string[] = [];
  const seenKept = new Set<string>();
  const seenDropped = new Set<string>();

  const ids = new Set([
    ...Object.keys(args.values),
    ...Object.keys(args.modes),
    ...Object.keys(args.notes),
  ]);

  for (const oldId of ids) {
    const prev = prevById.get(oldId);
    const byId = nextById.get(oldId);
    const byLabel = prev ? nextByLabel.get(normalizeLabel(prev.label)) : undefined;
    const dest = byId ?? byLabel;
    const sourceLabel = prev?.label?.trim() || "Unnamed calculation line";
    if (!dest) {
      if (!seenDropped.has(sourceLabel)) {
        seenDropped.add(sourceLabel);
        droppedLabels.push(sourceLabel);
      }
      continue;
    }
    if (Object.prototype.hasOwnProperty.call(args.values, oldId)) {
      values[dest.id] = args.values[oldId]!;
    }
    if (Object.prototype.hasOwnProperty.call(args.modes, oldId)) {
      modes[dest.id] = args.modes[oldId]!;
    }
    if (Object.prototype.hasOwnProperty.call(args.notes, oldId)) {
      notes[dest.id] = args.notes[oldId]!;
    }
    const keep = dest.label.trim() || sourceLabel;
    if (!seenKept.has(keep)) {
      seenKept.add(keep);
      keptLabels.push(keep);
    }
  }

  return { values, modes, notes, keptLabels, droppedLabels };
}

function asNumber(raw: unknown): number {
  const n = typeof raw === "number" ? raw : Number(raw);
  return Number.isFinite(n) ? n : 0;
}

function asAmountMode(raw: unknown, fallback: OpportunityAmountMode): OpportunityAmountMode {
  return raw === "percent" || raw === "amount" ? raw : fallback;
}

function asFormatKey(raw: unknown): OpportunityFormatKey {
  return raw === "generic" ? "generic" : "red_iron";
}

/** Snapshot/DB row → pricing inputs used when rerunning a product type. */
export function pricingInputsFromSnapshotRow(row: Record<string, unknown>): OpportunityPricingInputs {
  const calcValues =
    row.format_calc_values && typeof row.format_calc_values === "object" && !Array.isArray(row.format_calc_values)
      ? (row.format_calc_values as Record<string, number>)
      : {};
  const calcModes =
    row.format_calc_modes && typeof row.format_calc_modes === "object" && !Array.isArray(row.format_calc_modes)
      ? (row.format_calc_modes as Record<string, "fixed" | "percent">)
      : {};
  const calcNotes =
    row.format_calc_deviation_notes &&
    typeof row.format_calc_deviation_notes === "object" &&
    !Array.isArray(row.format_calc_deviation_notes)
      ? (row.format_calc_deviation_notes as Record<string, string>)
      : {};
  return {
    format_key: asFormatKey(row.format_key),
    building_materials_cost: asNumber(row.building_materials_cost),
    erection_cost: asNumber(row.erection_cost),
    concrete_cost: asNumber(row.concrete_cost),
    engineering_cost: asNumber(row.engineering_cost),
    permit_assistance_cost: asNumber(row.permit_assistance_cost),
    additional_items: Array.isArray(row.additional_items)
      ? (row.additional_items as OpportunityPricingInputs["additional_items"])
      : [],
    commission_pct: asNumber(row.commission_pct),
    margin_pct: asNumber(row.margin_pct),
    deposit_pct: asNumber(row.deposit_pct),
    subtotal_input: asNumber(row.subtotal_input),
    tax_mode: asAmountMode(row.tax_mode, "amount"),
    tax_value: asNumber(row.tax_value),
    deposit_mode: asAmountMode(row.deposit_mode, "percent"),
    deposit_amount_input: asNumber(row.deposit_amount_input ?? row.deposit_amount),
    sales_adjustments: Array.isArray(row.sales_adjustments)
      ? (row.sales_adjustments as OpportunityPricingInputs["sales_adjustments"])
      : [],
    deposit_deviation_note:
      typeof row.deposit_deviation_note === "string" && row.deposit_deviation_note.trim()
        ? row.deposit_deviation_note
        : null,
    format_calc_values: calcValues,
    format_calc_modes: calcModes,
    format_calc_deviation_notes: calcNotes,
  };
}
